import React, {
  InputHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { BsEye, BsEyeSlash } from "react-icons/bs";

import {
  Container,
  Error,
  Helper,
  InpuWrapError,
  Input,
  InputMask,
  Label,
} from "./styles";
import { IconType } from "react-icons";

type CustomInputProps = InputHTMLAttributes<HTMLInputElement> & {
  value?: string;
  labelText?: string;
  inputType?: string;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  onFocus?: (e: React.FocusEvent<any>) => void;
  icon?: IconType;
  msgError?: string;
  helperText?: string;
  errorCondition?: boolean;
  mask?: string;
  disabled?: boolean;
};

const CustomInput = ({
  labelText,
  inputType,
  onBlur,
  onFocus,
  icon: Icon,
  msgError,
  helperText,
  errorCondition,
  mask,
  disabled,
  ...rest
}: CustomInputProps): JSX.Element => {
  const [isFocus, setIsfocus] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [inputEmpty, setInputEmpty] = useState(true);

  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef && inputRef?.current) {
      const input = inputRef.current;
      const inputValue = input ? input.value || "" : "";
      setInputEmpty(inputValue.length === 0);
    }
  }, [inputRef, rest]);

  const showError = !!(errorCondition && msgError);
  const helperId = helperText && rest.id ? `${rest.id}-helper` : undefined;
  const errorId = showError && rest.id ? `${rest.id}-error` : undefined;
  const describedBy =
    [helperId, errorId].filter(Boolean).join(" ") || undefined;

  const type = useMemo(() => {
    if (inputType === "password" && showPass) {
      return "text";
    }
    return inputType;
  }, [showPass, inputType]);

  return (
    <InpuWrapError>
      <Container
        withIcon={!!Icon}
        whitError={errorCondition}
        disabled={disabled}
      >
        {Icon && <Icon size={26} />}
        <Label htmlFor={rest.id} isFocus={isFocus || !inputEmpty}>
          {labelText}
        </Label>
        {mask && (
          <InputMask
            disabled={disabled}
            mask={mask}
            ref={inputRef}
            type={type}
            aria-describedby={describedBy}
            aria-invalid={showError || undefined}
            onFocus={(e) => {
              setIsfocus(true);
              if (onFocus && typeof onFocus === "function") onFocus(e);
            }}
            onBlur={(e) => {
              setIsfocus(false);
              if (onBlur && typeof onBlur === "function") onBlur(e);
            }}
            {...rest}
          />
        )}
        {!mask && (
          <Input
            disabled={disabled}
            ref={inputRef}
            type={type}
            aria-describedby={describedBy}
            aria-invalid={showError || undefined}
            onFocus={(e) => {
              setIsfocus(true);
              if (onFocus && typeof onFocus === "function") onFocus(e);
            }}
            onBlur={(e) => {
              setIsfocus(false);
              if (onBlur && typeof onBlur === "function") onBlur(e);
            }}
            {...rest}
          />
        )}
        {inputType === "password" && (
          <>
            {!showPass && (
              <BsEyeSlash
                className="iconPass"
                size={26}
                onClick={() => setShowPass(!showPass)}
              />
            )}
            {showPass && (
              <BsEye
                className="iconPass"
                size={26}
                onClick={() => setShowPass(!showPass)}
              />
            )}
          </>
        )}
      </Container>
      {helperText && <Helper id={helperId}>{helperText}</Helper>}
      {showError && (
        <Error id={errorId} aria-live="polite">
          {msgError}
        </Error>
      )}
    </InpuWrapError>
  );
};

export default CustomInput;
